import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

export interface PolicyAcknowledgmentData {
  userId: string;
  userType: 'supabase' | 'internal';
  privacyPolicyAccepted: boolean;
  tosAccepted: boolean;
  cookiesAccepted: boolean;
  dataRightsAcknowledged: boolean;
  parentalConsent?: boolean;
  parentId?: string;
  parentName?: string;
  parentEmail?: string;
  ipAddress?: string;
  userAgent?: string;
  isMinorAcknowledgmentOnly?: boolean;
}

export interface PolicyVersion {
  policyType: 'privacy_policy' | 'terms_of_service';
  version: string;
  content: string;
  effectiveDate: string;
}

export const policyAcknowledgmentService = {
  /**
   * Check if user needs to acknowledge policies
   */
  async checkAcknowledgmentStatus(userId: string, userType: 'supabase' | 'internal') {
    try {
      const { data, error } = await supabase.rpc('needs_policy_acknowledgment', {
        p_user_id: userId,
        p_user_type: userType
      });

      if (error) throw error;
      return { needsAcknowledgment: data as boolean };
    } catch (error) {
      console.error('Error checking acknowledgment status:', error);
      return { needsAcknowledgment: true, error };
    }
  },

  /**
   * Check if user requires parental consent
   */
  async requiresParentalConsent(userId: string, userType: 'supabase' | 'internal') {
    try {
      const { data, error } = await supabase.rpc('requires_parental_consent', {
        p_user_id: userId,
        p_user_type: userType
      });

      if (error) throw error;
      return { required: data as boolean };
    } catch (error) {
      console.error('Error checking parental consent requirement:', error);
      return { required: false, error };
    }
  },

  /**
   * Get user's acknowledgment record
   */
  async getUserAcknowledgment(userId: string, userType: 'supabase' | 'internal') {
    try {
      const { data, error } = await supabase
        .from('user_policy_acknowledgments')
        .select('*')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching acknowledgment:', error);
      return { data: null, error };
    }
  },

  /**
   * Check parental consent verification status
   */
  async checkParentalConsentStatus(userId: string, userType: 'supabase' | 'internal') {
    try {
      const { data, error } = await supabase
        .from('user_policy_acknowledgments')
        .select('child_acknowledgment_only, parental_consent_verified, parental_consent_verified_at, parent_email')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .maybeSingle();

      if (error) throw error;
      
      return { 
        data: {
          isMinor: data?.child_acknowledgment_only || false,
          isVerified: data?.parental_consent_verified || false,
          verifiedAt: data?.parental_consent_verified_at || null,
          parentEmail: data?.parent_email || null
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error checking parental consent status:', error);
      return { data: null, error };
    }
  },

  /**
   * Submit policy acknowledgment (standard flow for adults)
   */
  async submitAcknowledgment(acknowledgmentData: PolicyAcknowledgmentData) {
    try {
      // Get latest policy versions
      const { data: versions } = await this.getLatestPolicyVersions();
      
      const privacyVersion = versions?.find(v => v.policy_type === 'privacy_policy')?.version || '1.0';
      const tosVersion = versions?.find(v => v.policy_type === 'terms_of_service')?.version || '1.0';

      // If this is a minor, use the minor acknowledgment flow
      if (acknowledgmentData.isMinorAcknowledgmentOnly) {
        return this.submitMinorAcknowledgment(acknowledgmentData, privacyVersion, tosVersion);
      }

      const { data, error } = await supabase
        .from('user_policy_acknowledgments')
        .upsert({
          user_id: acknowledgmentData.userId,
          user_type: acknowledgmentData.userType,
          privacy_policy_accepted: acknowledgmentData.privacyPolicyAccepted,
          tos_accepted: acknowledgmentData.tosAccepted,
          cookies_accepted: acknowledgmentData.cookiesAccepted,
          data_rights_acknowledged: acknowledgmentData.dataRightsAcknowledged,
          parental_consent: acknowledgmentData.parentalConsent,
          parent_id: acknowledgmentData.parentId,
          parent_name: acknowledgmentData.parentName,
          parent_email: acknowledgmentData.parentEmail,
          privacy_policy_version: privacyVersion,
          tos_version: tosVersion,
          ip_address: acknowledgmentData.ipAddress,
          user_agent: acknowledgmentData.userAgent,
          acknowledged_at: new Date().toISOString(),
          needs_reacknowledgment: false,
          child_acknowledgment_only: false,
          parental_consent_verified: true
        }, {
          onConflict: 'user_id,user_type'
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error submitting acknowledgment:', error);
      return { data: null, error };
    }
  },

  /**
   * Submit minor's acknowledgment (not consent) - requires parent verification
   */
  async submitMinorAcknowledgment(
    acknowledgmentData: PolicyAcknowledgmentData, 
    privacyVersion: string, 
    tosVersion: string
  ) {
    try {
      // Generate verification token
      const verificationToken = uuidv4();
      const tokenExpires = new Date();
      tokenExpires.setDate(tokenExpires.getDate() + 7); // 7 days expiry

      const { data, error } = await supabase
        .from('user_policy_acknowledgments')
        .upsert({
          user_id: acknowledgmentData.userId,
          user_type: acknowledgmentData.userType,
          privacy_policy_accepted: acknowledgmentData.privacyPolicyAccepted,
          tos_accepted: acknowledgmentData.tosAccepted,
          cookies_accepted: acknowledgmentData.cookiesAccepted,
          data_rights_acknowledged: acknowledgmentData.dataRightsAcknowledged,
          parental_consent: false, // Not yet consented
          parent_name: acknowledgmentData.parentName,
          parent_email: acknowledgmentData.parentEmail,
          privacy_policy_version: privacyVersion,
          tos_version: tosVersion,
          ip_address: acknowledgmentData.ipAddress,
          user_agent: acknowledgmentData.userAgent,
          acknowledged_at: new Date().toISOString(),
          needs_reacknowledgment: false,
          child_acknowledgment_only: true,
          parental_consent_verified: false,
          parental_consent_token: verificationToken,
          parental_consent_token_expires: tokenExpires.toISOString()
        }, {
          onConflict: 'user_id,user_type'
        })
        .select()
        .single();

      if (error) throw error;

      // Send parental consent verification email
      await this.sendParentalVerificationEmail(
        acknowledgmentData.userId,
        acknowledgmentData.parentName || '',
        acknowledgmentData.parentEmail || '',
        verificationToken
      );

      return { data, error: null, pendingVerification: true };
    } catch (error) {
      console.error('Error submitting minor acknowledgment:', error);
      return { data: null, error };
    }
  },

  /**
   * Send parental consent verification email
   */
  async sendParentalVerificationEmail(
    childUserId: string, 
    parentName: string, 
    parentEmail: string, 
    token: string
  ) {
    try {
      // Get child's username for the email
      let childName = 'your child';
      
      // Try to get from internal_users first
      const { data: internalUser } = await supabase
        .from('internal_users')
        .select('username')
        .eq('id', childUserId)
        .maybeSingle();
      
      if (internalUser?.username) {
        childName = internalUser.username;
      } else {
        // Try profiles for supabase users
        const { data: profile } = await supabase
          .from('profiles')
          .select('username')
          .eq('id', childUserId)
          .maybeSingle();
        
        if (profile?.username) {
          childName = profile.username;
        }
      }

      const { error } = await supabase.functions.invoke('send-parental-consent-email', {
        body: {
          childName,
          parentName,
          parentEmail,
          userId: childUserId,
          verificationToken: token
        }
      });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error sending parental verification email:', error);
      return { success: false, error };
    }
  },

  /**
   * Verify parental consent using token
   */
  async verifyParentalConsent(token: string, ipAddress?: string) {
    try {
      // Find the acknowledgment with this token
      const { data: acknowledgment, error: findError } = await supabase
        .from('user_policy_acknowledgments')
        .select('*')
        .eq('parental_consent_token', token)
        .maybeSingle();

      if (findError) throw findError;
      if (!acknowledgment) {
        return { success: false, error: 'Invalid or expired verification link' };
      }

      // Check if token is expired
      if (acknowledgment.parental_consent_token_expires) {
        const expiryDate = new Date(acknowledgment.parental_consent_token_expires);
        if (expiryDate < new Date()) {
          return { success: false, error: 'Verification link has expired. Please request a new one.' };
        }
      }

      // Update the acknowledgment to mark as verified
      const { data, error: updateError } = await supabase
        .from('user_policy_acknowledgments')
        .update({
          parental_consent: true,
          parental_consent_verified: true,
          parental_consent_verified_at: new Date().toISOString(),
          parental_consent_verified_ip: ipAddress,
          parental_consent_token: null, // Clear the token
          parental_consent_token_expires: null
        })
        .eq('id', acknowledgment.id)
        .select()
        .single();

      if (updateError) throw updateError;

      return { success: true, data };
    } catch (error) {
      console.error('Error verifying parental consent:', error);
      return { success: false, error };
    }
  },

  /**
   * Resend parental consent verification email
   */
  async resendParentalConsentEmail(userId: string, userType: 'supabase' | 'internal') {
    try {
      // Get current acknowledgment
      const { data: acknowledgment } = await this.getUserAcknowledgment(userId, userType);
      
      if (!acknowledgment) {
        return { success: false, error: 'No acknowledgment record found' };
      }

      if (acknowledgment.parental_consent_verified) {
        return { success: false, error: 'Parental consent already verified' };
      }

      // Generate new token
      const newToken = uuidv4();
      const tokenExpires = new Date();
      tokenExpires.setDate(tokenExpires.getDate() + 7);

      // Update with new token
      const { error: updateError } = await supabase
        .from('user_policy_acknowledgments')
        .update({
          parental_consent_token: newToken,
          parental_consent_token_expires: tokenExpires.toISOString()
        })
        .eq('user_id', userId)
        .eq('user_type', userType);

      if (updateError) throw updateError;

      // Send email
      await this.sendParentalVerificationEmail(
        userId,
        acknowledgment.parent_name || '',
        acknowledgment.parent_email || '',
        newToken
      );

      return { success: true };
    } catch (error) {
      console.error('Error resending parental consent email:', error);
      return { success: false, error };
    }
  },

  /**
   * Get latest policy versions
   */
  async getLatestPolicyVersions() {
    try {
      const { data, error } = await supabase.rpc('get_latest_policy_versions');

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching policy versions:', error);
      return { data: null, error };
    }
  },

  /**
   * Get policy content by type
   */
  async getPolicyContent(policyType: 'privacy_policy' | 'terms_of_service') {
    try {
      const { data, error } = await supabase
        .from('policy_versions')
        .select('*')
        .eq('policy_type', policyType)
        .eq('is_active', true)
        .order('effective_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching policy content:', error);
      return { data: null, error };
    }
  },

  /**
   * Get user's acknowledgment history (audit log)
   */
  async getAcknowledgmentHistory(userId: string, userType: 'supabase' | 'internal') {
    try {
      const { data, error } = await supabase
        .from('policy_acknowledgment_audit')
        .select('*')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching acknowledgment history:', error);
      return { data: null, error };
    }
  },

  /**
   * Send internal notification to parent(s) for consent
   */
  async sendParentConsentAlert(childUserId: string, childUsername: string) {
    try {
      // Find connected parent(s) for this child
      const { data: connections, error: connError } = await supabase
        .from('parent_child_connections')
        .select('parent_id')
        .eq('child_id', childUserId)
        .eq('is_active', true);

      if (connError) {
        console.error('Error finding parent connections:', connError);
        return { success: false, error: 'Could not find parent connection. Make sure your parent has connected to your account.' };
      }

      if (!connections || connections.length === 0) {
        return { success: false, error: 'No connected parent found. Ask your parent to connect to your account first.' };
      }

      // Send notification to each connected parent
      for (const connection of connections) {
        await supabase
          .from('notifications')
          .insert({
            user_id: connection.parent_id,
            user_type: 'internal',
            notification_type: 'parental_consent_required',
            category: 'account',
            title: 'Your Consent is Needed',
            message: `Your child ${childUsername} is awaiting your acknowledgment to continue using Players Lockeroom.`,
            priority: 'high',
            requires_action: true,
            action_url: '/parent-consent',
            metadata: {
              child_user_id: childUserId,
              child_username: childUsername
            }
          });
      }

      return { success: true, message: 'Alert sent to your parent!' };
    } catch (error) {
      console.error('Error sending parent consent alert:', error);
      return { success: false, error: 'Failed to send alert' };
    }
  },

  /**
   * Submit child acknowledgment (internal flow - no email)
   */
  async submitChildAcknowledgment(data: {
    userId: string;
    userType: 'supabase' | 'internal';
    privacyPolicyAccepted: boolean;
    tosAccepted: boolean;
    cookiesAccepted: boolean;
    dataRightsAcknowledged: boolean;
    userAgent?: string;
  }) {
    try {
      // Get latest policy versions
      const { data: versions } = await this.getLatestPolicyVersions();
      
      const privacyVersion = versions?.find((v: any) => v.policy_type === 'privacy_policy')?.version || '1.0';
      const tosVersion = versions?.find((v: any) => v.policy_type === 'terms_of_service')?.version || '1.0';

      // Get child's connection code
      const { data: codeData } = await supabase
        .from('child_connection_codes')
        .select('connection_code')
        .eq('child_user_id', data.userId)
        .eq('is_active', true)
        .maybeSingle();

      const { data: result, error } = await supabase
        .from('user_policy_acknowledgments')
        .upsert({
          user_id: data.userId,
          user_type: data.userType,
          privacy_policy_accepted: data.privacyPolicyAccepted,
          tos_accepted: data.tosAccepted,
          cookies_accepted: data.cookiesAccepted,
          data_rights_acknowledged: data.dataRightsAcknowledged,
          parental_consent: false,
          privacy_policy_version: privacyVersion,
          tos_version: tosVersion,
          user_agent: data.userAgent,
          acknowledged_at: new Date().toISOString(),
          needs_reacknowledgment: false,
          child_acknowledgment_only: true,
          parental_consent_verified: false,
          child_acknowledged: true,
          child_acknowledged_at: new Date().toISOString(),
          parent_acknowledged: false,
          child_connection_code: codeData?.connection_code || null
        }, {
          onConflict: 'user_id,user_type'
        })
        .select()
        .single();

      if (error) throw error;

      // Get child's username for notification
      let childUsername = 'Your child';
      const { data: childData } = await supabase
        .from('internal_users')
        .select('username')
        .eq('id', data.userId)
        .maybeSingle();
      
      if (childData?.username) {
        childUsername = childData.username;
      }

      // Send alert to connected parents
      await this.sendParentConsentAlert(data.userId, childUsername);

      return { success: true, data: result };
    } catch (error) {
      console.error('Error submitting child acknowledgment:', error);
      return { success: false, error };
    }
  },

  /**
   * Complete parent consent using connection code
   */
  async completeParentConsent(data: {
    childUserId: string;
    parentUserId: string;
    parentUserType: 'supabase' | 'internal';
    parentSignatureName: string;
    connectionCode: string;
  }) {
    try {
      // Verify the connection code matches
      const { data: codeData, error: codeError } = await supabase
        .from('child_connection_codes')
        .select('child_user_id')
        .eq('connection_code', data.connectionCode)
        .eq('is_active', true)
        .maybeSingle();

      if (codeError || !codeData) {
        return { success: false, error: 'Invalid connection code' };
      }

      if (codeData.child_user_id !== data.childUserId) {
        return { success: false, error: 'Connection code does not match this child' };
      }

      // Update the child's acknowledgment record
      const { error: updateError } = await supabase
        .from('user_policy_acknowledgments')
        .update({
          parental_consent: true,
          parental_consent_verified: true,
          parental_consent_verified_at: new Date().toISOString(),
          parent_acknowledged: true,
          parent_acknowledged_at: new Date().toISOString(),
          parent_user_id: data.parentUserId,
          parent_signature_name: data.parentSignatureName,
          child_connection_code: data.connectionCode
        })
        .eq('user_id', data.childUserId)
        .eq('user_type', 'internal')
        .eq('child_acknowledged', true);

      if (updateError) throw updateError;

      // Optionally create/update parent-child connection if not exists
      const { error: connError } = await supabase
        .from('parent_child_connections')
        .upsert({
          parent_id: data.parentUserId,
          child_id: data.childUserId,
          is_active: true,
          connected_at: new Date().toISOString()
        }, {
          onConflict: 'parent_id,child_id'
        });

      if (connError) {
        console.error('Error updating parent-child connection:', connError);
        // Non-fatal, continue
      }

      return { success: true };
    } catch (error) {
      console.error('Error completing parent consent:', error);
      return { success: false, error };
    }
  },

  /**
   * Check if child's parent has completed consent
   */
  async checkChildConsentStatus(userId: string, userType: 'supabase' | 'internal') {
    try {
      const { data, error } = await supabase
        .from('user_policy_acknowledgments')
        .select('child_acknowledged, parent_acknowledged, parent_acknowledged_at, parent_signature_name')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .maybeSingle();

      if (error) throw error;

      return {
        data: {
          childAcknowledged: data?.child_acknowledged || false,
          parentAcknowledged: data?.parent_acknowledged || false,
          parentAcknowledgedAt: data?.parent_acknowledged_at || null,
          parentSignature: data?.parent_signature_name || null
        },
        error: null
      };
    } catch (error) {
      console.error('Error checking child consent status:', error);
      return { data: null, error };
    }
  }
};
